/* eslint-disable import/order */
import { Box } from "@mui/material";
import { FC } from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { DraftReportPage } from "src/pages/draftReport";
import { ErpPartnersActsPage } from "src/pages/erpPartnersActs";
import { NotFound } from "src/pages/notFound";
import {
  CreatePartnerAgreementPage,
  PartnerAgreementPage,
  PartnerContractsPage,
} from "src/pages/partnerContracts";
import { NewPartnerPage, PartnerPage } from "src/pages/partners";
import { SignIn } from "src/pages/sign-in";
import { SummaryReportPage } from "src/pages/summaryReport";
import { ErpIframePageLayout } from "src/widgets/pageLayout/ui/";
import { PageLayout } from "src/widgets/pageLayout/ui/PageLayout";
import { AuthenticatedRoute } from "src/features/auth/ui/AuthenticatedRoute";
import {
  NewPartnerComplexContract,
  EitPartnerComplexContract,
  NewPartnerVirtualContract,
  EditPartnerVirtualContract,
} from "src/entities/partnerContracts";
import { Partner, PartnersList } from "src/entities/partners";
import { routes } from "src/shared/constants/routes.constants";
import { PrivateRoute } from "./PrivateRoute";
import { FinalReportPage } from "src/pages/finalReport";
import { ErpPartnersAgreementsPage } from "src/pages/erpPartnersAgreements/";
import { ErpPartnerClientsAccumulationPage } from "src/pages/erpPartnerClientsAccumulation";
import { ForbiddenPage } from "./ForbiddenPage";
import { PermissionRoute } from "./PermissionRoute";
import { PartnersDocDeptPage } from "src/pages/partnersDocDept";
import { ServiceToolsPage } from "src/pages";
import { ServiceToolsClientPage } from "src/pages/serviceToolsClient";
import { PermissionsPage } from "src/pages/permissions";
import { TransactionsPage } from "src/pages/transactions/ui/TransactionsPage";
import { IframeTransferMoneyPage } from "src/pages/iframe-transfer-money";
import { TargetingCountriesPage } from "src/pages/targetingCountries";

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <PrivateRoute>
        <PageLayout />
      </PrivateRoute>
    ),
    children: [
      {
        element: <AuthenticatedRoute />,
        children: [
          {
            index: true,
            element: <Box>Main</Box>,
          },
          { path: "/profile", element: <Box>Profile</Box> },
          { path: "/mailbox", element: <Box>Mailbox</Box> },
          {
            path: routes.transactions,
            element: (
              <PermissionRoute requiredpermissions="transactions.view">
                <TransactionsPage />
              </PermissionRoute>
            ),
          },
          {
            path: routes.partnersDocDept,
            element: (
              <PermissionRoute requiredpermissions={"crm:partners:doc_debt"}>
                <PartnersDocDeptPage />
              </PermissionRoute>
            ),
          },
          {
            path: routes.partners,
            element: <Outlet />,
            children: [
              {
                index: true,
                element: (
                  <PermissionRoute
                    requiredpermissions={[
                      "partners.view",
                      "partners.view.team",
                    ]}
                  >
                    <PartnersList />
                  </PermissionRoute>
                ),
              },
              {
                path: routes.newPartner,
                element: (
                  <PermissionRoute requiredpermissions="partners.create">
                    <NewPartnerPage />,
                  </PermissionRoute>
                ),
              },
              {
                path: routes.partnerItem,
                element: <PartnerPage />,
                children: [
                  {
                    index: true,
                    element: <Partner />,
                  },
                  {
                    path: routes.partnerContractsList,
                    element: (
                      <PermissionRoute requiredpermissions="partners.contract.edit">
                        <PartnerContractsPage />
                      </PermissionRoute>
                    ),
                    children: [
                      {
                        path: routes.partnerComplexContractCreate,
                        element: (
                          <PermissionRoute requiredpermissions="partners.contract.edit">
                            <NewPartnerComplexContract />
                          </PermissionRoute>
                        ),
                      },
                      {
                        path: routes.partnerComplexContractItem,
                        element: <EitPartnerComplexContract />,
                      },
                      {
                        path: routes.partnerVirtualContractCreate,
                        element: (
                          <PermissionRoute requiredpermissions="partners.contract.edit">
                            <NewPartnerVirtualContract />
                          </PermissionRoute>
                        ),
                      },
                      {
                        path: routes.partnerVirtualContractItem,
                        element: <EditPartnerVirtualContract />,
                      },
                      {
                        path: routes.partnerComplexContractAgreement,
                        element: <PartnerAgreementPage />,
                      },
                      {
                        path: routes.partnerComplexContractAgreementCreate,
                        element: (
                          <PermissionRoute requiredpermissions="partners.contract.edit">
                            <CreatePartnerAgreementPage />
                          </PermissionRoute>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: routes.summaryReport,
                element: (
                  <PermissionRoute
                    requiredpermissions={["partners.summary-report"]}
                  >
                    <SummaryReportPage />
                  </PermissionRoute>
                ),
              },
              {
                path: routes.draftReport,
                element: (
                  <PermissionRoute
                    requiredpermissions={["partners.draft-report"]}
                  >
                    <DraftReportPage />
                  </PermissionRoute>
                ),
              },
              {
                path: routes.finalReport,
                element: (
                  <PermissionRoute
                    requiredpermissions={["partners.final-report"]}
                  >
                    <FinalReportPage />
                  </PermissionRoute>
                ),
              },
            ],
          },
        ],
      },
    ],
    errorElement: <NotFound />,
  },
  { path: "/404", element: <NotFound /> },
  {
    path: "/auth/signin-kek",
    element: <SignIn />,
  },
  {
    path: routes.iframe,
    element: (
      <PrivateRoute>
        <ErpIframePageLayout />
      </PrivateRoute>
    ),
    children: [
      {
        element: <AuthenticatedRoute />,
        children: [
          {
            index: true,
            element: <Box>Erp</Box>,
          },
          {
            path: routes.iframePartnerFinancesActs,
            element: (
              <PermissionRoute requiredpermissions="crm.partners.finances">
                <ErpPartnersActsPage />
              </PermissionRoute>
            ),
          },
          {
            path: routes.iframePartnerContracts,
            element: (
              <PermissionRoute
                requiredpermissions={[
                  "partners.contract.edit",
                  "crm.partners.contacts",
                ]}
              >
                <ErpPartnersAgreementsPage />
              </PermissionRoute>
            ),
            children: [
              {
                path: routes.iframePartnerVirtualContractItem,
                element: (
                  <PermissionRoute requiredpermissions="partners.contract.edit">
                    <EditPartnerVirtualContract />,
                  </PermissionRoute>
                ),
              },
              {
                path: routes.iframePartnerComplexContractItem,
                element: (
                  <PermissionRoute requiredpermissions="partners.contract.edit">
                    <EitPartnerComplexContract />
                  </PermissionRoute>
                ),
              },
              {
                path: routes.iframePartnerComplexContractAgreement,
                element: (
                  <PermissionRoute requiredpermissions="partners.contract.edit">
                    <PartnerAgreementPage />
                  </PermissionRoute>
                ),
              },
              {
                path: routes.iframePartnerComplexContractAgreementCreate,
                element: (
                  <PermissionRoute requiredpermissions="partners.contract.edit">
                    <CreatePartnerAgreementPage />
                  </PermissionRoute>
                ),
              },
            ],
          },
          {
            path: routes.iframePartnerClientsAccumulation,
            element: (
              <PermissionRoute requiredpermissions="crm.partners.partner-clients">
                <ErpPartnerClientsAccumulationPage />
              </PermissionRoute>
            ),
          },
          {
            path: routes.iframeServiceTools,
            element: (
              <PermissionRoute
                requiredpermissions={[
                  "system:servicetools:directory:view",
                  "system:servicetools:edit",
                ]}
              >
                <ServiceToolsPage />
              </PermissionRoute>
            ),
          },
          {
            path: routes.iframeServiceToolsClient,
            element: (
              <PermissionRoute
                requiredpermissions={[
                  "crm:access:servicetools:view",
                  "crm:access:servicetools:edit",
                ]}
              >
                <ServiceToolsClientPage />
              </PermissionRoute>
            ),
          },
          {
            path: routes.iframePermissions,
            element: <PermissionsPage />,
          },
          {
            path: routes.iframeTransferMoney,
            element: <IframeTransferMoneyPage />,
          },
          {
            path: routes.iframeTargetingCountries,
            element: (
              <PermissionRoute requiredpermissions="system.targeting.view">
                <TargetingCountriesPage />
              </PermissionRoute>
            ),
          },
        ],
      },
    ],
    errorElement: <NotFound />,
  },
  {
    path: "/403",
    element: <ForbiddenPage />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);

export const Router: FC = () => <RouterProvider router={router} />;
